import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SignatureConfig } from 'interfaces-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Adv2Config } from '../../interfaces/iadv2config';
import { Adv2ConfigService } from '../../services/adv2-config.service';
import { CurrentUserOverrideStateService } from '../../services/current-user-override-state.service';

export enum SignatureConsentProgress {
  START,
  COMPLETE,
}

@Injectable({ providedIn: 'root' })
export class DocusignConsentService {
  constructor(
    private adv2ConfigService: Adv2ConfigService,
    private messageNotificationService: MessageNotificationService,
    private currentUserOverrideState: CurrentUserOverrideStateService,
  ) {}

  async checkConsent(
    workflowSignatureConfig: SignatureConfig,
    consentInProgress?: (status: SignatureConsentProgress) => void
  ): Promise<boolean> {
    const inProgress = consentInProgress ? consentInProgress : (_: SignatureConsentProgress) => {};
    let ok = true;
    try {
      const clientConfig = await this.adv2ConfigService.getConfig().toPromise();
      const signatureAccountConfig = this.applyWorkflowSignatureConfig(clientConfig, workflowSignatureConfig);

      await this.adv2ConfigService
        .testSignatureCredentials(signatureAccountConfig)
        .toPromise()
        .catch(async (reason: HttpErrorResponse) => {
          console.error('consent rejected', reason.status, reason.statusText, reason.error, reason.error?.error);
          if (reason.error?.error === 'consent_required') {
            inProgress(SignatureConsentProgress.START);
            ok = await this.requestConsent(signatureAccountConfig.providerName);
            inProgress(SignatureConsentProgress.COMPLETE);
          } else {
            ok = false;
            this.messageNotificationService.error(reason.error?.error ?? reason.statusText);
          }
        });
    } catch (error) {
      console.error('An error occurred during the consent process.', error);
      this.messageNotificationService.error('An error occurred during the consent process.');
      ok = false;
    }
    return Promise.resolve(ok);
  }

  private applyWorkflowSignatureConfig(clientConfig: Adv2Config, workflowSignatureConfig: SignatureConfig): any {
    const signatureAccountConfig = { ...clientConfig.signature };
    signatureAccountConfig.properties = workflowSignatureConfig.properties ? { ...workflowSignatureConfig.properties } : {};

    const override = this.currentUserOverrideState.current;
    if (override) {
      signatureAccountConfig.properties.currentUserOverride = { id: override.id, email: override.email };
    }

    return signatureAccountConfig;
  }

  private async requestConsent(providerName: string): Promise<boolean> {
    const consentUrl = await this.adv2ConfigService
      .getConsentUrl({
        callbackUrl: environment.uiHost,
        providerName: providerName,
      })
      .toPromise()
      .catch((reason) => {
        console.error('Failed to get consent url.', reason);
        this.messageNotificationService.error('Failed to get consent url.');
        return Promise.resolve(false);
      });

    const ok = await this.showRequestConsentPopup(consentUrl.url)
      .toPromise()
      .then((consentRequestSuccess) => {
        this.messageNotificationService.info(consentRequestSuccess);
        return true;
      })
      .catch((consentRequestError) => {
        this.messageNotificationService.error(consentRequestError);
        return false;
      });

    return Promise.resolve(ok);
  }

  private showRequestConsentPopup(consentUrl: string): Observable<any> {
    return new Observable((subscriber) => {
      const top = window.screenLeft + (window.outerWidth - 470) / 2;
      const left = window.screenTop + (window.outerHeight - 500) / 2;
      const consentWindow = window.open(consentUrl, '_blank', `location=no,toolbar=no,width=470,height=500,top=${top},left=${left}`);

      if (!consentWindow) {
        subscriber.error('Failed to launch Docusign consent window.');
        return;
      }

      const intervalId = setInterval(() => {
        if (consentWindow.closed) {
          console.log('Consent window closed.');
          window.removeEventListener('message', listener);
          window.clearInterval(intervalId);
          subscriber.error('Signature consent window closed.');
        }
      }, 500);

      const listener = (e: MessageEvent) => {
        console.log('event', e);
        if (e.data) {
          consentWindow.close();
          window.clearInterval(intervalId);
          window.removeEventListener('message', listener);

          if (e.data.docusignError) {
            const msg = e.data.docusignErrorMessage ?? 'Application consent failed.';
            subscriber.error(msg);
          } else if (e.data.responseUrl) {
            // responseUrl is just http://localhost:4200/?code=<code>
            subscriber.next('Thank you for providing consent.');
            subscriber.complete();
          }
        }
      };

      window.addEventListener('message', listener);
    });
  }
}
